/**
 * usePushSubscription — Web Push subscription lifecycle hook.
 *
 * Manages browser push permission, subscription creation/removal, and
 * syncing the subscription to the backend. Components only need to know
 * about four states and two actions — everything else (VAPID keys, Web
 * Crypto, service worker handshake) stays inside this hook.
 *
 * States
 * ──────
 *   'checking'     initial — reading the current SW subscription
 *   'unsupported'  browser has no PushManager / Notification API
 *                  (regular Safari, very old browsers, or iOS < 16.4)
 *   'denied'       user explicitly blocked notifications (can't re-ask)
 *   'unsubscribed' supported + permission not granted yet, or user opted out
 *   'subscribed'   active push subscription registered with backend
 *   'error'        subscribe() failed (bad key, SW not ready, network, etc.)
 *
 * iOS notes
 * ─────────
 * iOS 16.4+ supports Web Push only when the app is installed as a PWA
 * (added to Home Screen via Safari → Share → Add to Home Screen).
 * In a regular Safari browser tab, window.PushManager is absent and
 * the hook returns 'unsupported' — handled transparently.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import api from "@/api";

const PUSH_CHANGED = "vianbills:push-changed";

// ── VAPID key conversion ──────────────────────────────────────────────────────
// The browser requires applicationServerKey as a Uint8Array. The backend
// returns the key as a base64url string. This function converts between them.

function urlBase64ToUint8Array(base64String) {
  // A valid VAPID public key is an uncompressed P-256 point = 65 bytes = 87 base64url chars.
  // Anything shorter indicates a placeholder or misconfigured key.
  if (!base64String || base64String.length < 80) {
    throw new Error(
      `Invalid VAPID public key: expected ~87 base64url chars, got ${base64String?.length ?? 0}. ` +
      "Check the server's VAPID_PUBLIC_KEY configuration."
    );
  }
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64  = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw     = window.atob(base64);
  return Uint8Array.from(raw, (ch) => ch.charCodeAt(0));
}

function usesKey(subscription, key) {
  const saved = subscription.options?.applicationServerKey;
  if (!saved) return false;
  const bytes = new Uint8Array(saved);
  return bytes.length === key.length && bytes.every((byte, index) => byte === key[index]);
}

async function registerWithCurrentKey(reg, key) {
  const previous = await reg.pushManager.getSubscription();
  if (previous && usesKey(previous, key)) {
    const { data } = await api.post("/notifications/push/status", {
      endpoint: previous.endpoint,
    });
    if (data.active) return;
    // A 404/410 removed this endpoint on the server. Re-posting the same
    // browser token would make Push look enabled while delivery still fails.
  }
  if (previous) {
    // Chrome rejects subscribe() with a different applicationServerKey while
    // an old subscription exists. Remove the stale server record first.
    await api.delete("/notifications/push/unsubscribe", {
      data: { endpoint: previous.endpoint },
    });
    await previous.unsubscribe();
  }
  const next = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: key,
  });
  await api.post("/notifications/push/subscribe", next.toJSON());
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function usePushSubscription() {
  const [state,   setState]   = useState("checking");
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);   // human-readable error string
  const instance = useRef(Symbol("push-subscription"));

  // Recheck when another push control changes this browser's subscription.
  const check = useCallback(async (isActive = () => true) => {
    // Feature-detect all required APIs
    if (
      !("serviceWorker" in navigator) ||
      !("PushManager"   in window)    ||
      !("Notification"  in window)
    ) {
      if (isActive()) setState("unsupported");
      return;
    }

    if (Notification.permission === "denied") {
      if (isActive()) setState("denied");
      return;
    }

    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (!sub) {
        if (isActive()) {
          setError(null);
          setState("unsubscribed");
        }
        return;
      }
      // A stored browser subscription may use an old signing key. Rotate it
      // without asking permission again only when push was already enabled.
      const { data } = await api.get("/notifications/push/vapid-public-key");
      const key = urlBase64ToUint8Array(data.public_key);
      await registerWithCurrentKey(reg, key);
      if (isActive()) {
        setError(null);
        setState("subscribed");
      }
    } catch (err) {
      if (isActive()) {
        setError(err?.response?.data?.detail || err.message || "Could not check push subscription.");
        setState("error");
      }
    }
  }, []);

  useEffect(() => {
    let active = true;
    // Ignore a late response from an unmounted control.
    const refresh = () => { if (active) check(() => active); };
    refresh();
    const onChange = event => {
      if (event.detail !== instance.current) refresh();
    };
    window.addEventListener(PUSH_CHANGED, onChange);
    return () => { active = false; window.removeEventListener(PUSH_CHANGED, onChange); };
  }, [check]);

  // ── Subscribe ─────────────────────────────────────────────────────────────
  const subscribe = useCallback(async () => {
    if (!("PushManager" in window)) return;
    setLoading(true);
    setError(null);
    try {
      // Fetch configuration before asking for permission if push is disabled.
      let data;
      try {
        const res = await api.get("/notifications/push/vapid-public-key");
        data = res.data;
      } catch (fetchErr) {
        const msg = fetchErr?.response?.data?.detail || fetchErr?.message || "Could not reach the server";
        throw new Error(`Failed to fetch push configuration: ${msg}`);
      }

      // Convert + validate the key before handing to the browser.
      //    urlBase64ToUint8Array throws if the key is a placeholder or malformed.
      let appKey;
      try {
        appKey = urlBase64ToUint8Array(data.public_key);
      } catch (keyErr) {
        throw new Error(`Server push key is invalid — ${keyErr.message}`);
      }

      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        setState(permission === "denied" ? "denied" : "unsubscribed");
        return;
      }

      const reg = await navigator.serviceWorker.ready;
      try {
        await registerWithCurrentKey(reg, appKey);
      } catch (subErr) {
        // DOMException: "Registration failed" usually means the key is wrong
        // or the browser's push quota is exhausted.
        throw new Error(
          subErr.name === "NotAllowedError"
            ? "Push notification permission was denied."
            : `Browser subscription failed: ${subErr.message}`
        );
      }

      setState("subscribed");
      window.dispatchEvent(new CustomEvent(PUSH_CHANGED, { detail: instance.current }));

    } catch (err) {
      console.error("[Push] subscribe failed:", err);
      setError(err.message || "Push subscription failed. Please try again.");
      setState("error");
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Unsubscribe ───────────────────────────────────────────────────────────
  const unsubscribe = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        // Tell backend to remove this endpoint first (before browser forgets it)
        await api.delete("/notifications/push/unsubscribe", {
          data: { endpoint: sub.endpoint },
        });
        // Then unsubscribe in the browser
        await sub.unsubscribe();
      }
      setState("unsubscribed");
      window.dispatchEvent(new CustomEvent(PUSH_CHANGED, { detail: instance.current }));
    } catch (err) {
      console.error("[Push] unsubscribe failed:", err);
      setError(err.message || "Could not unsubscribe. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  // Allow retrying from error state
  const retry = useCallback(() => {
    setState("unsubscribed");
    setError(null);
  }, []);

  return { state, loading, error, subscribe, unsubscribe, retry };
}
